import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AppButton,
  AppTable,
  AppTableCell,
  AppTableHeadCell,
  AppTableRow,
  PageHeader,
  StatusChip,
  RowNumberCell,
} from "../../../../components/ui";
import { Box, IconButton, Tab, Tabs, Tooltip } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockResetIcon from "@mui/icons-material/LockReset";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import {
  ApplicationRole,
  ManagedUserResponse,
  UsersService,
} from "../../../../api";
import { TabsHelpers } from "../../../../components/basic/TabsHelpers";
import { isUserActive, toggleUserActive } from "../shared/userActiveActions";
import ResetPasswordDialog from "../shared/ResetPasswordDialog";
import { formatRole } from "../shared/roleLabels";
import { UsersTableFilters } from "./UsersFilters/UsersTableFilters";
import {
  extractEmailDomains,
  matchesUsersFilters,
  resetFilters,
} from "./UsersFilters/filtering";
import {
  EMPTY_USERS_FILTERS,
  type UsersFiltersState,
} from "./UsersFilters/types";

const MySwal = withReactContent(Swal);
const DEFAULT_ROWS_PER_PAGE = 25;

const TABLE_COLUMNS = [
  { id: "name", label: "Name", sortable: true },
  { id: "email", label: "Email address", sortable: true },
  { id: "phone", label: "Phone", sortable: true },
  { id: "roles", label: "Roles", sortable: true },
  { id: "status", label: "Status", sortable: true },
  { id: "joined", label: "Joined", sortable: true },
  { id: "updated", label: "Last updated", sortable: true },
];

type Order = "asc" | "desc";
type SortKey =
  | "index"
  | "name"
  | "email"
  | "phone"
  | "roles"
  | "status"
  | "joined"
  | "updated";

const iconBtnSx = { p: 0.35 };

function formatDate(value?: string | null) {
  if (!value) return "—";
  const date = dayjs(value);
  return date.isValid() ? date.format("MM/DD/YYYY") : "—";
}

function formatRolesText(roles?: ApplicationRole[] | null) {
  if (!roles?.length) return "";
  return roles.map(formatRole).join(", ");
}

function getSortValue(user: ManagedUserResponse, key: SortKey): string | number {
  switch (key) {
    case "index":
      return user.id;
    case "name":
      return `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim().toLowerCase();
    case "email":
      return (user.email ?? "").toLowerCase();
    case "phone":
      return (user.phoneNumber ?? "").toLowerCase();
    case "roles":
      return formatRolesText(user.roles).toLowerCase();
    case "status": {
      const active = isUserActive(user) ? 1 : 0;
      const pending = user.isPending ? 1 : 0;
      return active * 2 + pending;
    }
    case "joined":
      return user.createdAt ? dayjs(user.createdAt).valueOf() : 0;
    case "updated":
      return user.updatedAt ? dayjs(user.updatedAt).valueOf() : 0;
    default:
      return "";
  }
}

function compareUsers(
  a: ManagedUserResponse,
  b: ManagedUserResponse,
  orderBy: SortKey,
  order: Order
) {
  const aValue = getSortValue(a, orderBy);
  const bValue = getSortValue(b, orderBy);

  let result = 0;
  if (typeof aValue === "number" && typeof bValue === "number") {
    result = aValue - bValue;
  } else {
    result = String(aValue).localeCompare(String(bValue), undefined, {
      sensitivity: "base",
      numeric: true,
    });
  }

  return order === "asc" ? result : -result;
}

function matchesSearch(user: ManagedUserResponse, searchTerm: string) {
  const lower = searchTerm.toLowerCase();
  const fullName = `${user.firstName ?? ""} ${user.lastName ?? ""}`.toLowerCase();
  const rolesText = formatRolesText(user.roles).toLowerCase();

  return (
    fullName.includes(lower) ||
    (user.email?.toLowerCase().includes(lower) ?? false) ||
    (user.phoneNumber?.toLowerCase().includes(lower) ?? false) ||
    rolesText.includes(lower)
  );
}

export default function UsersTable() {
  const [value, setValue] = useState(0);
  const [users, setUsers] = useState<ManagedUserResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [usersFilters, setUsersFilters] =
    useState<UsersFiltersState>(EMPTY_USERS_FILTERS);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [orderBy, setOrderBy] = useState<SortKey>("name");
  const [order, setOrder] = useState<Order>("asc");
  const [resetPasswordUser, setResetPasswordUser] =
    useState<ManagedUserResponse | null>(null);
  const navigate = useNavigate();

  const loadUsers = useCallback(() => {
    setLoading(true);
    UsersService.postApiUsersGetManagedUsers({ includeInactive: true })
      .then(setUsers)
      .catch(() => {
        setUsers([]);
        MySwal.fire({
          icon: "error",
          title: <p>Could not load users.</p>,
          text: "If this is a new deployment, make sure the database migration for user deactivation has been applied.",
        });
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const activeUsers = useMemo(
    () => users.filter((user) => isUserActive(user)),
    [users]
  );
  const inactiveUsers = useMemo(
    () => users.filter((user) => !isUserActive(user)),
    [users]
  );

  const tabUsers = value === 0 ? activeUsers : inactiveUsers;

  const roleOptions = useMemo(
    () =>
      Object.values(ApplicationRole)
        .sort((a, b) => formatRole(a).localeCompare(formatRole(b)))
        .map((role) => ({ value: role, label: formatRole(role) })),
    []
  );

  const emailDomainOptions = useMemo(
    () => extractEmailDomains(users),
    [users]
  );

  const applyUsersFilters = (next: UsersFiltersState) => {
    setUsersFilters(next);
    setPage(0);
  };

  const clearUsersFilters = () => {
    applyUsersFilters(resetFilters());
  };

  const filteredUsers = useMemo(() => {
    let list = tabUsers;
    if (searchTerm.trim()) {
      list = list.filter((user) => matchesSearch(user, searchTerm.trim()));
    }
    list = list.filter((user) => matchesUsersFilters(user, usersFilters));
    return [...list].sort((a, b) => compareUsers(a, b, orderBy, order));
  }, [tabUsers, searchTerm, usersFilters, orderBy, order]);

  const paginatedUsers = useMemo(
    () =>
      filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredUsers, page, rowsPerPage]
  );

  const handleRequestSort = (columnId: string) => {
    const property = columnId as SortKey;
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setPage(0);
  };

  const handleToggleActive = async (
    user: ManagedUserResponse,
    isActive: boolean
  ) => {
    await toggleUserActive(user.id, {
      firstName: user.firstName,
      lastName: user.lastName,
      isActive,
      onSuccess: loadUsers,
    });
  };

  const handleOpenUser = (user: ManagedUserResponse) => {
    navigate(`/staff-users/${user.id}`);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <PageHeader
        title="Users"
        subtitle="Manage accounts, roles, access, and passwords."
        actions={
          <AppButton
            variant="contained"
            color="primary"
            onClick={() => navigate("/generateLink?setAny=true")}
          >
            Create registration link
          </AppButton>
        }
      />

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={value}
          onChange={(_, newValue) => {
            setValue(newValue);
            setPage(0);
          }}
        >
          <Tab
            label={`ACTIVE (${activeUsers.length})`}
            {...TabsHelpers.parseProps(0)}
          />
          <Tab
            label={`INACTIVE (${inactiveUsers.length})`}
            {...TabsHelpers.parseProps(1)}
          />
        </Tabs>
      </Box>

      <Box sx={{ mb: 2 }}>
        <UsersTableFilters
          filters={usersFilters}
          searchValue={searchTerm}
          onSearchChange={(value) => {
            setSearchTerm(value);
            setPage(0);
          }}
          onApply={applyUsersFilters}
          onClear={clearUsersFilters}
          roleOptions={roleOptions}
          emailDomainOptions={emailDomainOptions}
        />
      </Box>

      <AppTable
        columns={TABLE_COLUMNS}
        showRowNumber
        sortBy={orderBy}
        sortAsc={order === "asc"}
        onRequestSort={handleRequestSort}
        loading={loading}
        loadingMessage="Loading users..."
        isEmpty={!loading && filteredUsers.length === 0}
        emptyTitle="No users match the selected filters."
        extraColumnCount={1}
        trailingHeadCells={
          <AppTableHeadCell align="right" sx={{ width: 120 }}>
            Actions
          </AppTableHeadCell>
        }
        pagination={{
          count: filteredUsers.length,
          page,
          rowsPerPage,
          rowsPerPageOptions: [10, 25, 50, 100],
          onPageChange: setPage,
          onRowsPerPageChange: (next) => {
            setRowsPerPage(next);
            setPage(0);
          },
        }}
      >
        {paginatedUsers.map((user, index) => (
          <AppTableRow
            tabIndex={-1}
            key={user.id}
            onClick={() => handleOpenUser(user)}
            sx={{ cursor: "pointer" }}
          >
            <RowNumberCell value={page * rowsPerPage + index + 1} />
            <AppTableCell>
              {user.firstName} {user.lastName}
            </AppTableCell>
            <AppTableCell>{user.email}</AppTableCell>
            <AppTableCell>{user.phoneNumber || "—"}</AppTableCell>
            <AppTableCell>
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {(user.roles ?? []).length === 0
                  ? "—"
                  : (user.roles ?? []).map((role) => (
                      <StatusChip
                        key={role}
                        label={formatRole(role)}
                        tone="secondary"
                      />
                    ))}
              </Box>
            </AppTableCell>
            <AppTableCell>
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {isUserActive(user) ? (
                  <StatusChip label="Active" tone="dirtyGreen" />
                ) : (
                  <StatusChip label="Inactive" tone="neutral" variant="outlined" />
                )}
                {user.isPending && (
                  <StatusChip label="Pending" tone="secondary" />
                )}
              </Box>
            </AppTableCell>
            <AppTableCell>{formatDate(user.createdAt)}</AppTableCell>
            <AppTableCell>{formatDate(user.updatedAt)}</AppTableCell>
            <AppTableCell
              align="right"
              onClick={(e) => e.stopPropagation()}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.25,
                }}
              >
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    color="primary"
                    sx={iconBtnSx}
                    onClick={() => handleOpenUser(user)}
                    aria-label="Edit user"
                  >
                    <EditOutlinedIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reset password">
                  <IconButton
                    size="small"
                    color="primary"
                    sx={iconBtnSx}
                    onClick={() => setResetPasswordUser(user)}
                    aria-label="Reset password"
                  >
                    <LockResetIcon />
                  </IconButton>
                </Tooltip>
                {isUserActive(user) ? (
                  <Tooltip title="Deactivate">
                    <IconButton
                      size="small"
                      color="error"
                      sx={iconBtnSx}
                      onClick={() => handleToggleActive(user, false)}
                      aria-label="Deactivate user"
                    >
                      <PersonOffOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title="Reactivate">
                    <IconButton
                      size="small"
                      color="primary"
                      sx={iconBtnSx}
                      onClick={() => handleToggleActive(user, true)}
                      aria-label="Reactivate user"
                    >
                      <HowToRegOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </AppTableCell>
          </AppTableRow>
        ))}
      </AppTable>

      <ResetPasswordDialog
        open={!!resetPasswordUser}
        user={resetPasswordUser}
        onClose={() => setResetPasswordUser(null)}
      />
    </Box>
  );
}
